import { inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { take } from 'rxjs/operators';
import { switchMap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PcoLogItem, PCO_STATUS } from '../pco/pco.types';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { primeDetailsSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { PrimeCommitmentsApiService } from '@app/services/prime-commitments-api.service';
import { NotificationsService } from '@app/services/notifications.service';
import { COR_STATUS, CORLogItem } from '@app/store/prime-commitments/prime-commitments.types';

type ChangeActionsState = {
  pco: {
    allPCOs: PcoLogItem[];
    isLoading: boolean;
    lastPage: number;
    lastPageEmpty: boolean;
    searchText: string;
    isEdit: boolean;
  };
  cor: {
    allCORs: CORLogItem[];
    isLoading: boolean;
    lastPage: number;
    lastPageEmpty: boolean;
    searchText: string;
    isEdit: boolean;
  };
};

/**
 * This store handles non-assigned PCOs and CORs for the Prime Changes interaction bar.
 * It manages the loading, searching, editing states of PCOs and CORs.
 * It manages pagination and search too.
 */
@Injectable({ providedIn: 'root' })
export class ChangeActionsStore {
  readonly primeService = inject(PrimeCommitmentsApiService);
  readonly store = inject(Store);
  readonly notif = inject(NotificationsService);

  projectId$ = this.store.select(viewProjectSelectors.selectProjectId);
  corDetails$ = this.store.select(primeDetailsSelectors.selectCORDetailsSidebar);

  pccoDetails$ = this.store.select(primeDetailsSelectors.selectPCCOData);
  isPCCOEditMode$ = this.store.select(primeDetailsSelectors.selectIsPCCOEditMode);

  private readonly initialState: ChangeActionsState = {
    pco: {
      allPCOs: [],
      isLoading: false,
      lastPage: null,
      lastPageEmpty: false,
      searchText: '',
      isEdit: false,
    },
    cor: {
      allCORs: [],
      isLoading: false,
      lastPage: null,
      lastPageEmpty: false,
      searchText: '',
      isEdit: false,
    },
  };

  readonly state = signalState<ChangeActionsState>({ ...this.initialState });

  private readonly pco = this.state.pco;
  readonly pcos = this.state.pco.allPCOs;
  readonly pcosLoading = this.state.pco.isLoading;

  private readonly cor = this.state.cor;
  readonly cors = this.state.cor.allCORs;
  readonly corsLoading = this.state.cor.isLoading;

  loadPCOs(loadFirstPage = true, onlyIfEmpty = false) {
    if (this.pco.lastPageEmpty() && !loadFirstPage) {
      return;
    }

    if (onlyIfEmpty && this.pco.allPCOs().length > 0) {
      return;
    }

    patchState(this.state, {
      pco: {
        ...this.pco(),
        isLoading: true,
      },
    });
    const page = loadFirstPage ? 1 : this.pco.lastPage() + 1;
    this.projectId$
      .pipe(
        take(1),
        switchMap((projectId) =>
          this.primeService.getAllPCOs(projectId, {
            perPage: 20,
            page,
            isAssigned: 0,
            search: this.pco.searchText(),
            statuses: [PCO_STATUS.OPEN],
          }),
        ),
        concatLatestFrom(() => this.corDetails$),
      )
      .subscribe({
        next: ([loadedPCOs, corDetail]) => {
          patchState(this.state, {
            pco: {
              ...this.pco(),
              isLoading: false,
              lastPage: page,
              lastPageEmpty: loadedPCOs.length === 0,
            },
          });

          const pcos = [];

          if (this.cor.isEdit() && corDetail && loadFirstPage) {
            // the PCOs alreqdy linked to the COR should appear in the list, though they're not available for selection
            pcos.push(...corDetail.pcos);
          }
          if (!loadFirstPage) {
            pcos.push(...this.pco().allPCOs);
          }
          pcos.push(...loadedPCOs);
          patchState(this.state, {
            pco: {
              ...this.pco(),
              allPCOs: pcos,
            },
          });

          // if (this.pco.isEdit() && corDetail && loadFirstPage) {
          //   // to retrigger the total count calculation...
          //   this.selectedPCOIds$.next(this.selectedPCOIds);
          // }
        },
        error: (error) => {
          patchState(this.state, {
            pco: {
              ...this.pco(),
              isLoading: false,
            },
          });
          this.notif.showError(
            `An error occurred while loading PCOs. ${error?.error?.message ?? ''}`,
          );
        },
      });
  }

  loadCORs(loadFirstPage = true, onlyIfEmpty = false) {
    if (this.cor.lastPageEmpty() && !loadFirstPage) {
      return;
    }

    if (onlyIfEmpty && this.cor.allCORs().length > 0) {
      return;
    }

    patchState(this.state, {
      cor: {
        ...this.cor(),
        isLoading: true,
      },
    });
    const page = loadFirstPage ? 1 : this.cor.lastPage() + 1;
    this.projectId$
      .pipe(
        take(1),
        switchMap((projectId) =>
          this.primeService.getAllCORs(projectId, {
            perPage: 20,
            page,
            isAssigned: 0,
            search: this.cor.searchText(),
            statuses: [COR_STATUS.APPROVED],
          }),
        ),
        concatLatestFrom(() => [this.pccoDetails$, this.isPCCOEditMode$]),
      )
      .subscribe({
        next: ([loadedCORs, pccoDetails, isPCCOEdit]) => {
          patchState(this.state, {
            cor: {
              ...this.cor(),
              isLoading: false,
              lastPage: page,
              lastPageEmpty: loadedCORs.length === 0,
            },
          });

          const cors = [];
          if (isPCCOEdit && pccoDetails && loadFirstPage) {
            // the CORs already linked to the PCCO should appear in the list, though they're not available for selection
            cors.push(...pccoDetails.cors);
          }
          if (!loadFirstPage) {
            cors.push(...this.cor.allCORs());
          }
          cors.push(...loadedCORs);

          patchState(this.state, {
            cor: {
              ...this.cor(),
              allCORs: cors,
            },
          });

          // if (isPCCOEdit && pccoDetails && loadFirstPage) {
          //   // the PCOs alreqdy linked to the COR should appear in the list, though they're not available for selection
          //   this.selectedCORIds$.next(pccoDetails.cors.map((cor) => cor.id));
          // }
        },
        error: (error) => {
          patchState(this.state, {
            cor: {
              ...this.cor(),
              isLoading: false,
            },
          });
          this.notif.showError(
            `An error occurred while loading more CORs. ${error?.error?.message ?? ''}`,
          );
        },
      });
  }

  pcoIsEditMode(isEdit: boolean) {
    console.log('ChangeActionsStore: pcoIsEditMode', isEdit);
    patchState(this.state, {
      pco: {
        ...this.pco(),
        isEdit,
      },
    });
  }

  corIsEditMode(isEdit: boolean) {
    console.log('ChangeActionsStore: corIsEditMode', isEdit);
    patchState(this.state, {
      cor: {
        ...this.cor(),
        isEdit,
      },
    });
  }

  pcoSearchTextChanged(searchText: string) {
    patchState(this.state, {
      pco: {
        ...this.pco(),
        searchText,
      },
    });
    this.loadPCOs();
  }

  corSearchTextChanged(searchText: string) {
    patchState(this.state, {
      cor: {
        ...this.cor(),
        searchText,
      },
    });
    this.loadCORs();
  }

  onProjectDestroy() {
    patchState(this.state, { ...this.initialState });
  }
}
